/**
 * SegregationPage Component
 * 
 * Main page for the Tinder-like A/B classification feature.
 * Supports three views:
 * - "All": ParallaxDeck with infinite loading
 * - "Bucket A": Paginated list of users classified as A
 * - "Bucket B": Paginated list of users classified as B
 */

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ParallaxDeck, { DeckProfile } from './ParallaxDeck';
import AnalyticsApiService, { PaginatedUsersResponse, SegregationUserItem } from '../../services/analyticsApi';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Pagination } from '../ui/pagination';
import { ImageWithFallback } from '../figma/ImageWithFallback';

/**
 * UserTableRow - Table row component for list views (Bucket A/B)
 */
function UserTableRow({ user, onSelect, bucket }: { user: SegregationUserItem; onSelect: (sel: 'A' | 'B') => void; bucket: 'A' | 'B' }) {
  const fullName = [user.fname, user.lname].filter(Boolean).join(' ') || `User ${user.id}`;

  return (
    <tr className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      {/* Profile Picture */}
      <td className="px-6 py-4">
        <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700">
          {user.pfp_url ? (
            <ImageWithFallback src={user.pfp_url} alt={fullName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm font-semibold">
              {fullName.slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
      </td>

      {/* Name & ID */}
      <td className="px-6 py-4">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">{fullName}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">ID: {user.id}</span>
        </div>
      </td>

      {/* Age & Country */}
      <td className="px-6 py-4">
        <div className="flex flex-col">
          <span className="text-sm text-gray-900 dark:text-white">
            {user.age ? `${user.age} years` : '—'}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {user.phone_country || '—'}
          </span>
        </div>
      </td>

      {/* Bio */}
      <td className="px-6 py-4 max-w-xs">
        <p className="text-sm text-gray-700 dark:text-gray-300 truncate" title={user.bio || ''}>
          {user.bio || '—'}
        </p>
      </td>

      {/* Social Links */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          {user.linkedin_url && (
            <a 
              href={user.linkedin_url} 
              target="_blank" 
              rel="noreferrer" 
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs"
              title="LinkedIn"
            >
              LI
            </a>
          )}
          {user.twitter_url && (
            <a 
              href={user.twitter_url} 
              target="_blank" 
              rel="noreferrer" 
              className="text-sky-600 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300 text-xs"
              title="Twitter"
            >
              TW
            </a>
          )}
          {user.instagram_url && (
            <a 
              href={user.instagram_url} 
              target="_blank" 
              rel="noreferrer" 
              className="text-pink-600 hover:text-pink-800 dark:text-pink-400 dark:hover:text-pink-300 text-xs"
              title="Instagram"
            >
              IG
            </a>
          )}
          {!user.linkedin_url && !user.twitter_url && !user.instagram_url && (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="px-6 py-4">
        <div className="flex flex-col">
          <span className={`text-xs px-2 py-1 rounded-full inline-block w-fit ${
            user.onboarded 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
          }`}>
            {user.onboarded ? 'Onboarded' : 'Pending'}
          </span>
        </div>
      </td>

      {/* Actions */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          {bucket === 'A' ? (
            // Show only opposite-bucket action (move to B)
            <Button 
              onClick={() => onSelect('B')} 
              size="sm"
              variant="outline"
              className="px-3 py-1 text-xs border-gray-300 dark:border-gray-600"
            >
              B
            </Button>
          ) : (
            // bucket === 'B' → show only action to move to A
            <Button 
              onClick={() => onSelect('A')} 
              size="sm"
              className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white"
            >
              A
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

/**
 * Map SegregationUserItem to DeckProfile
 */
function mapItemsToProfiles(items: SegregationUserItem[]): DeckProfile[] {
  const prefix = window.location.origin.replace(/\/$/, '');
  return items.map((u) => ({
    id: String(u.id),
    name: [u.fname, u.lname].filter(Boolean).join(' ').trim() || `User ${u.id}`,
    age: u.age ?? undefined,
    bio: u.bio ?? null,
    photo: u.pfp_url && u.pfp_url.startsWith('http') ? u.pfp_url : `${prefix}${u.pfp_url ?? ''}`,
    instagramUrl: u.instagram_url || null,
    twitterUrl: u.twitter_url || null,
    linkedinUrl: u.linkedin_url || null,
  }));
}

/**
 * Main SegregationPage Component
 */
export default function SegregationPage() {
  // Pagination state for list views (Bucket A/B)
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [data, setData] = useState<PaginatedUsersResponse | null>(null);

  // Deck state
  const allItemsRef = useRef<SegregationUserItem[]>([]); // Store items in ref to prevent re-renders
  const [visibleItemsVersion, setVisibleItemsVersion] = useState(0); // Trigger updates only when needed
  const [deckIndex, setDeckIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [nextPageToFetch, setNextPageToFetch] = useState(3); // Start at page 3 (after initial 2)
  const prefetchingRef = useRef(false);
  const deckInitializedRef = useRef(false); // Prevent multiple initializations

  // UI state
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bucket, setBucket] = useState<'A' | 'B' | undefined>(undefined);

  /**
   * Initialize deck for "All" view
   * - Optimistic loading: show UI after first page, continue loading in background
   * - Load multiple pages for smooth experience
   */
  const initDeck = useCallback(async () => {
    if (bucket !== undefined) return; // Only for "all" deck
    if (deckInitializedRef.current) return; // Already initialized
    
    deckInitializedRef.current = true;
    setLoading(true);
    setError(null);
    
    try {
      // Load saved deck position from backend
      const deckState = await AnalyticsApiService.getSegregationDeckState('all');
      const savedIndex = deckState.current_index;

      console.log(`Resuming deck at saved position: index ${savedIndex}`);

      // Reserve initial pages using reservation-backed API, hydrate by IDs
      const initialPages = 2;  // Load only 2 pages (100 users) initially
      const initResp = await AnalyticsApiService.initSegregationDeck('all', 50, initialPages);
      const hydrated = initResp.items.length
        ? await AnalyticsApiService.getSegregationUsersByIds(initResp.items)
        : [];

      allItemsRef.current = hydrated;

      // Show UI
      setLoading(false);
      setBackgroundLoading(false);

      // Resume at saved position, but clamp
      const startIndex = Math.min(savedIndex, Math.max(0, allItemsRef.current.length - 1));
      setDeckIndex(startIndex);
      setVisibleItemsVersion((v) => v + 1);

      // Compute next page to fetch safely
      setNextPageToFetch(initialPages + 1);
      setHasMore(hydrated.length > 0);

      console.log(`Deck initialization complete with ${hydrated.length} items`);
    } catch (e: any) {
      setError(e?.message || 'Failed to init deck');
      console.error('initDeck error:', e);
      deckInitializedRef.current = false; // Allow retry on error
      setLoading(false);
      setBackgroundLoading(false);
    }
  }, [bucket]);

  /**
   * Load more users for infinite loading
   * - Fetch 2 pages (100 users) at a time
   * - Triggered when ≤20 items remain (near end of current batch)
   * - New batch added only after previous batch consumed
   */
  const reserveMore = useCallback(async () => {
    if (bucket !== undefined || prefetchingRef.current || !hasMore) return;
    prefetchingRef.current = true;
    setBackgroundLoading(true);
    
    const pagesToFetch = 2; // Fetch 2 pages (100 users) when running low
    
    try {
      const page = nextPageToFetch;
      console.log(`🔄 Fetching more users: pages ${page}-${page + pagesToFetch - 1}`);
      
      const reserveResp = await AnalyticsApiService.reserveMoreSegregation('all', 50, pagesToFetch, page);
      const ids = reserveResp.items || [];
      const hydrated = ids.length ? await AnalyticsApiService.getSegregationUsersByIds(ids) : [];

      if (hydrated.length > 0) {
        // Append new batch to existing profiles
        allItemsRef.current = [...allItemsRef.current, ...hydrated];
        setNextPageToFetch((prev) => prev + pagesToFetch);
        setVisibleItemsVersion((v) => v + 1); // Trigger re-render to show new cards
        console.log(`✅ Loaded ${hydrated.length} more users. Total: ${allItemsRef.current.length}, Next page: ${page + pagesToFetch}`);
      } else {
        setHasMore(false);
        console.log(`⚠️ No more users available from backend`);
      }
    } catch (e: any) {
      console.error('❌ reserveMore error:', e);
      setHasMore(false);
    } finally {
      prefetchingRef.current = false;
      setBackgroundLoading(false);
    }
  }, [bucket, nextPageToFetch, hasMore]);

  /**
   * Handle bucket filter change or page mount
   */
  useEffect(() => {
    // "All" deck: init; A/B: use traditional list
    if (bucket === undefined) {
      // Only reset and init if switching back to "All" view or first mount
      if (!deckInitializedRef.current) {
        setData(null);
        setPage(1);
        initDeck();
      }
      } else {
      // Switching to Bucket A or B - reset deck initialization flag
      deckInitializedRef.current = false;
      setLoading(true);
      setError(null);
      AnalyticsApiService.getSegregationUsers(page, limit, bucket)
        .then((res) => {
          setData(res);
          allItemsRef.current = res.items;
          setVisibleItemsVersion((v) => v + 1);
        })
        .catch((e) => {
          setError(e?.message || 'Failed to load users');
          console.error('getSegregationUsers error:', e);
        })
        .finally(() => setLoading(false));
    }
  }, [bucket, page, limit]); // Removed initDeck from dependencies to prevent re-render loop

  /**
   * Auto-reserve more when deck runs low (when 20 or fewer cards remain)
   * Fetches 2 more pages (100 users) when threshold reached
   * This ensures we never run out of cards during classification
   * 
   * Note: Also triggered in handleDeckSelect after each selection
   */
  useEffect(() => {
    const remainingCards = allItemsRef.current.length - deckIndex;
    
    // Trigger on initial load or when deckIndex changes
    if (bucket === undefined && hasMore && remainingCards <= 20 && !prefetchingRef.current) {
      console.log(`🔔 Threshold check: ${remainingCards} cards remaining, fetching more...`);
      reserveMore();
    }
  }, [deckIndex, hasMore, bucket, reserveMore]);

  /**
   * Handle A/B selection from list view
   */
  const handleSelect = async (u: SegregationUserItem, sel: 'A' | 'B') => {
    try {
      await AnalyticsApiService.markSegregation(u.id, sel);
      // Optimistically remove the selected user from list
      setData((prev) => (prev ? { ...prev, items: prev.items.filter((it) => it.id !== u.id) } : prev));
      
      const filteredItems = allItemsRef.current.filter((it) => it.id !== u.id);
      allItemsRef.current = filteredItems;
      
      // Adjust deck index if needed
      if (deckIndex >= filteredItems.length && filteredItems.length > 0) {
        setDeckIndex(filteredItems.length - 1);
      }
      
      setVisibleItemsVersion((v) => v + 1);
    } catch (e: any) {
      setError(e?.message || 'Failed to record selection');
      console.error('handleSelect error:', e);
    }
  };

  /**
   * Map allItems to DeckProfile format
   * Only recalculate when visibleItemsVersion changes (not on every background load)
   */
  const deckProfiles = useMemo(() => mapItemsToProfiles(allItemsRef.current), [visibleItemsVersion]);

  /**
   * Handle A/B selection from ParallaxDeck
   * Uses optimistic updates for instant UI response
   */
  const handleDeckSelect = async (profileId: string, sel: 'A' | 'B') => {
    const idNum = Number(profileId);
    if (!Number.isFinite(idNum)) return;
    
    // OPTIMISTIC UPDATE: Remove card immediately for instant UI response
    const filteredItems = allItemsRef.current.filter((it) => it.id !== idNum);
    allItemsRef.current = filteredItems;
    
    // Adjust index if needed
    if (deckIndex >= filteredItems.length && filteredItems.length > 0) {
      setDeckIndex(filteredItems.length - 1);
    }
    
    // INSTANT: Trigger deck update to show next card immediately
    setVisibleItemsVersion((v) => v + 1);
    
    // Calculate remaining for threshold check
    const remainingCards = filteredItems.length - deckIndex;
    console.log(`Selected ${sel}, ${filteredItems.length} total, ${remainingCards} remaining, index: ${deckIndex}`);
    
    // Check threshold for background loading
    if (hasMore && remainingCards <= 20 && !prefetchingRef.current) {
      console.log(`🔔 Threshold reached after selection, fetching more cards...`);
      reserveMore(); // Non-blocking background fetch
    }
    
    // Backend persistence happens in background (non-blocking)
    try {
      await AnalyticsApiService.markSegregation(idNum, sel);
      await AnalyticsApiService.updateDeckState('all', deckIndex);
    } catch (e: any) {
      // If backend fails, log error but don't revert UI (optimistic)
      console.error('Background save failed:', e);
      setError(`Failed to save selection: ${e?.message || 'Unknown error'}`);
      // TODO: Could implement retry queue or revert optimistic update
    }
  };

  return (
    <div className="h-full w-full relative">
      {/* Header and controls - always visible, overlay on top */}
      <div className="absolute top-0 left-0 right-0 px-6 py-4 bg-gradient-to-b from-slate-50/95 to-transparent dark:from-gray-950/95 dark:to-transparent backdrop-blur-md" style={{ zIndex: 10 }}>
        <div className="w-full mx-auto">
          {/* Header Card */}
          <div className="mb-4 rounded-2xl border border-black/5 dark:border-white/10 bg-white/70 dark:bg-gray-800/40 backdrop-blur-xl shadow-sm px-5 py-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">Segregation</h2>
                <p className="text-sm text-slate-600 dark:text-gray-300">Tinder-like A/B classification of VectorDB users</p>
              </div>
              {bucket === undefined && allItemsRef.current.length > 0 && (
                <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-gray-300">
                  <div className="rounded-full px-3 py-1 bg-white/60 dark:bg-gray-700/50 border border-black/5 dark:border-white/10 shadow-sm">
                    {deckIndex + 1} / {allItemsRef.current.length}
                  </div>
                  {backgroundLoading && (
                    <div className="rounded-full px-3 py-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 shadow-sm flex items-center gap-2">
                      <motion.div
                        className="w-2 h-2 rounded-full bg-blue-500"
                        animate={{ opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                      />
                      <span className="text-xs">Loading more...</span>
                    </div>
                  )}
                </div>
              )}
              {bucket !== undefined && data && (
                <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-gray-300">
                  <div className="rounded-full px-3 py-1 bg-white/60 dark:bg-gray-700/50 border border-black/5 dark:border-white/10 shadow-sm">
                    Total: {data.total ?? 0}
                  </div>
                  <div className="rounded-full px-3 py-1 bg-white/60 dark:bg-gray-700/50 border border-black/5 dark:border-white/10 shadow-sm">
                    Page {data.page} / {data.total_pages || 1}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Filter Pills */}
          <div className="flex items-center gap-3 mb-2">
            <span className="text-sm text-slate-700 dark:text-gray-300">Filter:</span>
            <div className="inline-flex items-center gap-1 rounded-full bg-white/70 dark:bg-gray-800/50 border border-black/5 dark:border-white/10 backdrop-blur-xl p-1 shadow-sm">
              <Button
                variant={bucket === undefined ? 'default' : 'outline'}
                className={`rounded-full px-4 ${bucket === undefined ? '' : 'bg-transparent'}`}
                onClick={() => {
                  setPage(1);
                  setBucket(undefined);
                }}
              >
                All
              </Button>
              <Button
                variant={bucket === 'A' ? 'default' : 'outline'}
                className={`rounded-full px-4 ${bucket === 'A' ? '' : 'bg-transparent'}`}
                onClick={() => {
                  setPage(1);
                  setBucket('A');
                }}
              >
                Bucket A
              </Button>
              <Button
                variant={bucket === 'B' ? 'default' : 'outline'}
                className={`rounded-full px-4 ${bucket === 'B' ? '' : 'bg-transparent'}`}
                onClick={() => {
                  setPage(1);
                  setBucket('B');
                }}
              >
                Bucket B
              </Button>
            </div>
          </div>

          {/* Error Display */}
          <AnimatePresence>
            {error && (
              <motion.div 
                className="mb-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-2 text-center text-red-600 dark:text-red-400 text-sm font-medium"
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Loading Display */}
          <AnimatePresence>
            {loading && (
              <motion.div 
                className="text-sm text-gray-500 text-center py-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <motion.div
                  animate={{ 
                    opacity: [0.4, 1, 0.4],
                  }}
                  transition={{ 
                    duration: 1.5, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  Loading…
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Content area - deck or list */}
      <AnimatePresence mode="wait">
        {!loading && (deckProfiles.length > 0 || allItemsRef.current.length > 0) && (
          <>
            {bucket === undefined ? (
              <motion.div 
                className="fixed inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20 dark:from-gray-950 dark:via-blue-950/20 dark:to-purple-950/10" 
                style={{ zIndex: 5 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ParallaxDeck profiles={deckProfiles} index={deckIndex} onChoose={handleDeckSelect} />
              </motion.div>
            ) : (
            <div className="h-full overflow-y-auto px-6 pt-48 pb-8 bg-gradient-to-b from-slate-50 to-slate-100 dark:from-gray-950 dark:to-gray-900">
              <div className="w-full mx-auto">
                {/* Table Container */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {/* Table Header */}
                  <div className="bg-gray-50 dark:bg-gray-900/50 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {bucket === 'A' ? 'Bucket A Users' : 'Bucket B Users'}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {data?.total || 0} users found
                    </p>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-900/30">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Photo
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Age & Country
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Bio
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Social
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {allItemsRef.current.map((item: SegregationUserItem) => (
                          bucket ? (
                            <UserTableRow 
                              key={item.id} 
                              user={item} 
                              bucket={bucket}
                              onSelect={(sel) => handleSelect(item, sel)} 
                            />
                          ) : null
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Empty State */}
                  {allItemsRef.current.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-gray-500 dark:text-gray-400">
                        <div className="text-lg font-medium mb-2">No users found</div>
                        <div className="text-sm">
                          {bucket === 'A' 
                            ? 'No users have been classified as Bucket A yet.' 
                            : 'No users have been classified as Bucket B yet.'
                          }
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {data && data.total_pages && data.total_pages > 1 && (
                  <div className="mt-6">
                    <Pagination
                      currentPage={page}
                      totalPages={data.total_pages}
                      onPageChange={setPage}
                      hasNext={data.has_next}
                      hasPrev={data.has_prev}
                      total={data.total}
                      limit={data.limit}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </>
        )}
      </AnimatePresence>
    </div>
  );
}

